import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput, HintTurnInputWithOutputs } from '../../types';

const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_MS = 5 * 60_000;
import {
    OperationDetailPage,
    ContextGrid,
    UserProfileCard,
    DetailItem,
    MetadataCard,
    MetaItem,
    QuestionCard,
    StudentWorkCard,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import { LatexText } from '@shared-ui/latex';
import { ImageViewer, OptionChip } from '@shared-ui/components';
import './SolutionAnalysisDetail.css';
import './HintDetail.css';

/** A run row decorated with the 1-based hint turn it came from. Lets the runs
 *  table show a "Turn" column and lets us look up that turn's HintHistoryEntry
 *  + canvas snapshot when the row is selected. */
type RunWithTurn = ModelOutput & { turnIndex: number };

/** Loosely-typed view of a HintHistoryEntry. The hint history field on
 *  HintRequest is `Array<Record<string, unknown>>` so we narrow at the use site. */
interface HintHistoryEntryView {
    /** Absent on LLM turns written before the history became a union. */
    source?: 'llm' | 'prewritten';
    /** Prewritten reveals only — 0-based position in `question.hints[]`. */
    hintIndex?: number;
    hint?: string;
    category?: string;
    level?: string;
    detectedState?: string;
    detectedIssue?: string;
    yourProgress?: string;
    difficulty?: string;
    confidence?: number;
    createdAt?: string;
    workChanged?: boolean;
    studentMessage?: string;
    feedback?: { rating?: 'positive' | 'negative'; comment?: string; submittedAt?: string };
}

export function HintDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    // Cap auto-refresh while status='generating' so a stuck doc doesn't poll
    // forever. Mirrors the SolutionAnalysisDetail pattern.
    const mountedAtRef = useRef<number>(Date.now());

    const { data: hintRequest, isLoading, error, refetch } = useQuery({
        queryKey: ['hintRequest', id],
        queryFn: () => adminApi.getHintRequest(id!),
        enabled: !!id,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            const elapsed = Date.now() - mountedAtRef.current;
            return status === 'generating' && elapsed < POLL_MAX_MS ? POLL_INTERVAL_MS : false;
        },
    });

    // Fallback for pre-cutover docs: the new endpoint returns inputsByTurn,
    // but if it's missing (e.g. legacy proxy or rolled-back backend), fetch
    // the latest input alone so the page still renders something useful.
    const needsLegacyFetch = !!hintRequest && hintRequest.inputsByTurn === undefined;
    const legacyInputId = needsLegacyFetch ? hintRequest?.modelInputId : undefined;
    const { data: legacyInputWithOutputs } = useQuery({
        queryKey: ['modelInput', legacyInputId],
        queryFn: () => adminApi.getModelInput(legacyInputId!),
        enabled: !!legacyInputId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });

    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    // Build per-turn inputs + flat run list.
    // Prefer the new inputsByTurn payload; fall back to the legacy single-input
    // fetch (which we tag as turn 1 so the UI doesn't break).
    const inputsByTurn: HintTurnInputWithOutputs[] = useMemo(() => {
        if (hintRequest?.inputsByTurn?.length) return hintRequest.inputsByTurn;
        if (legacyInputWithOutputs) {
            return [{
                ...legacyInputWithOutputs,
                turnIndex: 1,
                outputs: legacyInputWithOutputs.outputs ?? [],
            }];
        }
        return [];
    }, [hintRequest?.inputsByTurn, legacyInputWithOutputs]);

    const runs: RunWithTurn[] = useMemo(
        () => inputsByTurn.flatMap(input =>
            input.outputs.map(output => ({ ...output, turnIndex: input.turnIndex })),
        ),
        [inputsByTurn],
    );

    // Default-select the latest turn's primary output once runs are loaded.
    // Showing the most recent hint's primary first matches the prior behavior
    // and the user's mental model ("what's the latest result").
    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const latestTurn = runs.reduce((max, r) => Math.max(max, r.turnIndex), 0);
        const latestRuns = runs.filter(r => r.turnIndex === latestTurn);
        const primary = hintRequest?.modelOutputId
            ? latestRuns.find(r => r.id === hintRequest.modelOutputId) ?? latestRuns[0]
            : latestRuns.find(r => r.isPrimary) ?? latestRuns[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId, hintRequest?.modelOutputId]);

    const selectedRun = runs.find(r => r.id === selectedRunId);
    // Oldest first. LLM turns align with inputsByTurn by their LLM ordinal;
    // prewritten reveals sit in the same array but have no ModelInput.
    const hintHistory = (hintRequest?.hintHistory ?? []) as unknown as HintHistoryEntryView[];

    const getStateBadge = (state?: string) => {
        if (!state) return <span className="badge unknown">-</span>;
        const colors: Record<string, string> = {
            no_attempt: 'badge-gentle',
            progressed: 'badge-conceptual',
            stuck: 'badge-moderate',
            confused: 'badge-calculation',
            error: 'badge-direct',
            needs_clarification: 'badge-encouragement',
        };
        return <span className={`badge ${colors[state] || 'unknown'}`}>{state.replace(/_/g, ' ')}</span>;
    };
    const getDifficultyBadge = (difficulty?: string) => {
        if (!difficulty) return <span className="badge unknown">-</span>;
        const colors: Record<string, string> = {
            easy: 'badge-gentle',
            medium: 'badge-moderate',
            hard: 'badge-direct',
        };
        return <span className={`badge ${colors[difficulty] || 'unknown'}`}>{difficulty}</span>;
    };

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading hint details...</div></div>;
    }
    if (error || !hintRequest) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading hint: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={() => navigate('/submissions?tab=hints')}>Back to Hints</button>
            </div>
        );
    }

    const elapsedMs = Date.now() - mountedAtRef.current;
    const pollingExhausted = hintRequest.status === 'generating' && elapsedMs >= POLL_MAX_MS;

    if (hintRequest.status === 'generating') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Hint generation in progress…</h2>
                <p>The current turn's LLM call is still in flight. The page will refresh automatically.</p>
                <p style={{ color: '#888', fontSize: '0.9rem' }}>
                    Session: <code>{hintRequest.id}</code>
                </p>
                {pollingExhausted && (
                    <>
                        <p style={{ color: '#888', marginTop: '1rem' }}>
                            Auto-refresh stopped after {Math.round(POLL_MAX_MS / 60_000)} minutes — generation may be stuck.
                        </p>
                        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => { mountedAtRef.current = Date.now(); refetch(); }}>
                            Refresh
                        </button>
                    </>
                )}
            </div>
        );
    }

    if (hintRequest.status === 'failed') {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h2>Hint generation failed</h2>
                <p>The LLM call for the most recent turn did not complete on session <code>{hintRequest.id}</code>.</p>
                <button type="button" style={{ marginTop: '1rem' }} onClick={() => refetch()}>
                    Refresh
                </button>
            </div>
        );
    }

    const promptLookup = { getPromptName, getPromptType };
    const baseColumns = buildDefaultRunColumns<RunWithTurn>(promptLookup, {
        returnTo: { label: 'Hint Detail' },
    });
    // Prepend a "Turn" column so multi-turn sessions show which hint each run
    // belongs to. Hidden in the layout (single column) when there's only one
    // turn — clutter for the common case of one hint per session.
    const showTurnColumn = inputsByTurn.length > 1;
    const runColumns = showTurnColumn
        ? [
            {
                header: 'Turn',
                render: (r: RunWithTurn) => <span className="badge">#{r.turnIndex}</span>,
            },
            ...baseColumns,
        ]
        : baseColumns;
    // Augment the heading with a "Turn #N" badge when there are multiple turns,
    // so an earlier run's detail is clearly attributed to its hint turn.
    const runDetailHeading = selectedRun && (
        <>
            {showTurnColumn && (
                <span className="badge" style={{ marginRight: 8 }}>Turn #{selectedRun.turnIndex}</span>
            )}
            {buildRunDetailHeading(selectedRun, promptLookup)}
        </>
    );
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedFeedbackHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));
    // Inner parsed body — the shared ModelOutputView (via RunDetailCard) owns the collapsible wrapper.
    const parsedFeedbackBody = selectedRun && parsedFeedbackHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
        />
    ) : undefined;

    return (
        <OperationDetailPage
            title="Hint Request Details"
            id={hintRequest.id}
            idContainer="hint-requests"
            backLabel="Hints"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Hint" />}
            onBack={() => navigate('/submissions?tab=hints')}
            context={
                <ContextGrid
                    left={
                        <UserProfileCard
                            user={{
                                id: hintRequest.user?.id,
                                name: hintRequest.user?.name,
                                email: hintRequest.user?.email,
                                grade: hintRequest.user?.grade,
                                // Pipeline config shown in the Hint & Result card — omit here.
                            }}
                            onViewUser={uid => navigate(`/users/${uid}`)}
                        >
                            <DetailItem label="Hints in Session">
                                {hintRequest.hintHistory?.length ?? 0}
                            </DetailItem>
                        </UserProfileCard>
                    }
                    right={
                        <MetadataCard title="Hint & Result">
                            <MetaItem label="Pipeline Config"><span className="tag">{hintRequest.pipelineConfig || '—'}</span></MetaItem>
                            <MetaItem label="Subject"><span className="tag">{hintRequest.question?.subject || '-'}</span></MetaItem>
                            <MetaItem label="Topic"><span className="tag">{hintRequest.question?.topic || '-'}</span></MetaItem>
                            <MetaItem label="Provider"><span className="tag">{hintRequest.llmProvider || '-'}</span></MetaItem>
                            <MetaItem label="Latency">{formatLatency(hintRequest.latencyMs)}</MetaItem>
                            <MetaItem label="Submitted">
                                <span className="datetime">{format(new Date(hintRequest.createdAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                        </MetadataCard>
                    }
                />
            }
            input={
                <>
                    <QuestionCard
                        question={{
                            id: hintRequest.questionId,
                            questionText: hintRequest.question?.questionText,
                            questionDiagramUrl: hintRequest.question?.questionDiagramUrl,
                            figures: hintRequest.question?.figures,
                            questionType: hintRequest.question?.questionType,
                            parts: hintRequest.question?.parts,
                        }}
                        onViewQuestion={qid => navigate(`/questions?id=${qid}`)}
                    />
                    <StudentWorkCard
                        work={{
                            ...hintRequest,
                            // selectedOption isn't on hintRequest itself —
                            // pull it from the latest model input.
                            selectedOption: inputsByTurn.at(-1)?.studentSolution?.selectedOption,
                        }}
                        questionOptions={hintRequest.question?.options}
                        title="Latest Student Solution"
                    />
                </>
            }
            operationSummary={
                <>
                    {/* Per-turn breakdown — one card per hint with the canvas the
                     *  student had drawn at that turn. Falls back to a single
                     *  "Hint Generated" card on legacy docs that have no
                     *  hintHistory. */}
                    {hintHistory.length > 0 ? (
                        <div className="card">
                            <h3>Hint History ({hintHistory.length})</h3>
                            {hintHistory.map((entry, idx) => {
                                // `inputsByTurn` has one entry per LLM turn, so it aligns
                                // with the LLM ordinal — NOT with the position in
                                // hintHistory, which also holds prewritten reveals.
                                const isPrewritten = entry.source === 'prewritten';
                                const llmOrdinal = hintHistory
                                    .slice(0, idx + 1)
                                    .filter(e => e.source !== 'prewritten').length;
                                const turnInput = isPrewritten
                                    ? undefined
                                    : inputsByTurn.find(i => i.turnIndex === llmOrdinal);
                                const turnCanvas = turnInput?.studentSolution?.imageUrl;
                                const turnSizeBytes = turnInput?.studentSolution?.imageSizeBytes;
                                const turnTypedText = turnInput?.studentSolution?.textAnswer;
                                const turnSelectedOption = turnInput?.studentSolution?.selectedOption;
                                const isLatest = idx === hintHistory.length - 1;
                                const hasWork = !!(turnCanvas || turnTypedText || turnSelectedOption);
                                // One collapsible card per turn, split into what went INTO
                                // the model (resolved category/level, work-changed flag,
                                // student message, student work) and what came OUT
                                // (diagnosis + hint). Only the latest turn starts open.
                                return (
                                    <details key={`turn-${idx}`} className="hint-turn" open={isLatest}>
                                        <summary className="hint-turn-summary">
                                            <strong>
                                                {isPrewritten ? `Ready-made hint #${(entry.hintIndex ?? 0) + 1}` : `Turn #${llmOrdinal}`}
                                            </strong>
                                            {entry.createdAt && (
                                                <span className="datetime hint-turn-time">
                                                    {format(new Date(entry.createdAt), 'MMM dd, HH:mm:ss')}
                                                </span>
                                            )}
                                            {!isPrewritten && (
                                                <span className="hint-turn-glance">
                                                    {[entry.category?.replace(/_/g, ' '), entry.level, entry.detectedState].filter(Boolean).join(' · ')}
                                                </span>
                                            )}
                                            {entry.feedback?.rating && (
                                                <span
                                                    className="badge"
                                                    title={entry.feedback.comment || undefined}
                                                    style={{
                                                        background: entry.feedback.rating === 'positive'
                                                            ? 'rgba(22, 163, 74, 0.18)'
                                                            : 'rgba(220, 38, 38, 0.18)',
                                                        color: entry.feedback.rating === 'positive' ? '#16a34a' : '#dc2626',
                                                    }}
                                                >
                                                    {entry.feedback.rating === 'positive' ? '👍 Helpful' : '👎 Not helpful'}
                                                </span>
                                            )}
                                        </summary>

                                        {!isPrewritten && (
                                            <fieldset className="hint-turn-section">
                                                <legend>Input</legend>
                                                <div className="hint-turn-fields">
                                                    <span><label>Category:</label> {entry.category?.replace(/_/g, ' ') || '-'}</span>
                                                    <span><label>Level:</label> {entry.level || '-'}</span>
                                                    <span>
                                                        <label>Work changed:</label>{' '}
                                                        {entry.workChanged === undefined ? '-' : entry.workChanged ? 'yes' : 'no'}
                                                    </span>
                                                </div>
                                                {entry.studentMessage && (
                                                    <p className="hint-turn-note"><label>Student asked:</label> “{entry.studentMessage}”</p>
                                                )}
                                                <fieldset className="hint-turn-section hint-turn-work">
                                                    <legend>Student work</legend>
                                                    {!hasWork && <p className="hint-turn-empty">No student work captured.</p>}
                                                    {turnSelectedOption && (() => {
                                                        const opt = hintRequest.question?.options?.find(
                                                            o => o.label?.toLowerCase() === turnSelectedOption.toLowerCase(),
                                                        );
                                                        return <OptionChip label={opt?.label || turnSelectedOption} value={opt?.text} color="accent" />;
                                                    })()}
                                                    {turnCanvas && (
                                                        <div className="hint-turn-image">
                                                            <ImageViewer src={turnCanvas} alt="Student's work" className="canvas-image" />
                                                            {typeof turnSizeBytes === 'number' && (
                                                                <span className="hint-turn-caption">
                                                                    {hintRequest.inputMode === 'photo' ? 'Photo' : 'Canvas'} · {Math.round(turnSizeBytes / 1024)} KB
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {turnTypedText && <pre className="hint-turn-typed">{turnTypedText}</pre>}
                                                </fieldset>
                                            </fieldset>
                                        )}

                                        <fieldset className="hint-turn-section">
                                            <legend>Output</legend>
                                            {!isPrewritten && (
                                                <div className="hint-turn-fields">
                                                    <span><label>State:</label> {getStateBadge(entry.detectedState)}</span>
                                                    <span><label>Difficulty:</label> {getDifficultyBadge(entry.difficulty)}</span>
                                                    <span>
                                                        <label>Confidence:</label>{' '}
                                                        {typeof entry.confidence === 'number' ? entry.confidence.toFixed(2) : '-'}
                                                    </span>
                                                </div>
                                            )}
                                            {!isPrewritten && entry.detectedIssue && (
                                                <p className="hint-turn-note"><label>Detected issue:</label> <LatexText text={entry.detectedIssue} /></p>
                                            )}
                                            {!isPrewritten && entry.yourProgress && (
                                                <p className="hint-turn-note"><label>Progress:</label> <LatexText text={entry.yourProgress} /></p>
                                            )}
                                            {entry.hint && (
                                                <div className="hint-content hint-turn-block">
                                                    <p><LatexText text={entry.hint} /></p>
                                                </div>
                                            )}
                                            {entry.feedback?.comment && (
                                                <div className="hint-content hint-turn-block hint-turn-feedback">
                                                    <p>Student feedback: “{entry.feedback.comment}”</p>
                                                </div>
                                            )}
                                        </fieldset>
                                    </details>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="card">
                            <h3>Hint Generated</h3>
                            <div className="metadata-grid">
                                <div className="meta-item">
                                    <label>State</label>
                                    {getStateBadge(hintRequest.detectedState)}
                                </div>
                                <div className="meta-item">
                                    <label>Category</label>
                                    <span className="badge">{hintRequest.category?.replace(/_/g, ' ') || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <label>Level</label>
                                    <span className="badge">{hintRequest.level || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <label>Difficulty</label>
                                    {getDifficultyBadge(hintRequest.difficulty)}
                                </div>
                            </div>
                            <div className="hint-content" style={{ marginTop: 12 }}>
                                <p><LatexText text={hintRequest.hint} /></p>
                            </div>
                        </div>
                    )}
                </>
            }
            runs={runs.length > 0 && (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={r => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={r => setSelectedRunId(r.id)}
                    getRowClass={r => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            )}
            runDetail={selectedRun && (
                <RunDetailCard
                    heading={runDetailHeading}
                    error={runError}
                    errorRecords={selectedRun.errorRecords}
                    summary={runSummary}
                    systemPrompt={selectedRun.systemPromptText}
                    userPrompt={selectedRun.promptText}
                    rawResponse={selectedRunRawResponse}
                    rawOutputFull={selectedRun.rawOutputFull}
                    reasoningSummary={selectedRun.reasoningSummaryText}
                    modelInputId={selectedRun.inputId}
                    modelOutputId={selectedRun.id}
                    parsed={parsedFeedbackBody}
                />
            )}
        />
    );
}
